import { AddressContract } from '../../types/Address';
import { CartTotalsContract } from '../../types/Cart';
import { CustomerCreateContract } from '../../types/Customer';

import { PayPalServiceConfig } from '../../types/ServiceConfigurations';
import { ConfigEnabledPaymentMethodContract } from '../../types/Configuration';
import ServiceError from '../../models/classes/ServiceError';
import { Endpoints } from '../../utils/constants';
import {
  IPayPalService,
  PayPalInfoContract,
  PayPalAcpHostedFieldsSubmitResponse,
} from '../../types/PayPal';
import { PayPalAcp3dsAction, find3dsAction } from '../PayPal3ds';

/* eslint-disable */
export type PayPalPaymentPayerInfo = {
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
};

export type PayPalPaymentError = {
  count: number | null;
  debugId: string | null;
  message: string | null;
};

export type PayPalPaymentPayer = {
  payer_info?: PayPalPaymentPayerInfo | null;
  email_address?: string | null;
  payment_method: string;
};
export type PayPalPaymentTransactionAmountDetails = {
  tax: string;
  shipping: string;
  subtotal: string;
};
export type PayPalPaymentTransactionAmount = {
  currencyValue: string;
  total: string;
  details: PayPalPaymentTransactionAmountDetails;
};
// This is actually the ItemList not the AddressContainer
// V1 has never sent the list of items in the cart (not required)
export type PayPalPaymentAddressContainer = {
  shipping_address?: {
    recipient_name?: string | null;
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    country_code?: string | null;
    postal_code?: string | null;
    phone?: string | null;
    state?: string | null;
  };
  shipping_phone_number?: string | null;
};
export type PayPalPaymentTransaction =
  | {
      Amount: PayPalPaymentTransactionAmount;
      amount?: never;
      item_list?: PayPalPaymentAddressContainer | null;
    }
  | {
      Amount?: never;
      amount: PayPalPaymentTransactionAmount;
      item_list?: PayPalPaymentAddressContainer | null;
    };
export type PayPalPaymentRedirectUrls = {
  // these are not used by the Smart Buttons SDK but required for some reason
  return_url: string;
  cancel_url: string;
};
export type PayPalPaymentTotals = {
  total: string;
  subtotal: string;
  shipping: string;
  tax: string;
};
export type PayPalCreatePaymentResponse = {
  token: string;
  errors: {
    count: number;
    message: string;
  };
};

export type PayPalPaymentData = {
  intent: string;
  errors?: PayPalPaymentError;
  payer: PayPalPaymentPayer | null;
  transactions: PayPalPaymentTransaction;
  redirect_urls: PayPalPaymentRedirectUrls;
  id: string | undefined;
  state: string | undefined;
  token: string | undefined;
};

type PayPalAcpGetClientTokenResponse =
  | {
      ClientToken: string;
      clientToken: never;
    }
  | { clientToken: string; ClientToken: never };

const getNamesFromPaymentData = (
  details: PayPalPaymentData
): { firstName: string; lastName: string } => {
  const paypalCustomer = details.payer?.payer_info;
  const backupNames =
    details.transactions?.item_list?.shipping_address?.recipient_name?.split(
      ' '
    );
  const firstName = paypalCustomer?.first_name ?? backupNames?.[0] ?? '';
  backupNames?.shift();
  const lastName = paypalCustomer?.last_name ?? backupNames?.join(' ') ?? '';
  return { firstName, lastName };
};

class PayPalService implements IPayPalService {
  private storeUrl: string;
  public useV2Endpoints: boolean;

  private cachedDetails: PayPalPaymentData | null = null;

  constructor(config: PayPalServiceConfig) {
    this.storeUrl = config.apiUrl;
    this.useV2Endpoints = false;
  }

  public getPaymentDetails = async (
    id: string
  ): Promise<PayPalPaymentData | null> => {
    if (!this.cachedDetails) {
      const detailsEndpoint = this.useV2Endpoints
        ? `${Endpoints.PAYPAL_ORDER}/${id}`
        : `${Endpoints.PAYPAL_GET_PAYMENT_DETAILS}${id}`;
      try {
        const uri = `${this.storeUrl}${detailsEndpoint}`;
        const response = await window.fetch(uri);
        const payment = await response.json();
        this.cachedDetails = payment as PayPalPaymentData;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        return null;
      }
    }
    return this.cachedDetails;
  };

  public createPayment = async (
    paymentRequest: PayPalPaymentData
  ): Promise<PayPalCreatePaymentResponse> => {
    const result = await this.sendCreatePayment(paymentRequest);

    if (result.errors.count > 0) {
      if (result.errors.message?.includes('VALIDATION_ERROR')) {
        const newRequest = { ...paymentRequest };
        delete newRequest.transactions.item_list;
        return this.sendCreatePayment(newRequest);
      }
    }
    return result;
  };

  public getClientIdentityToken = async (): Promise<string | null> => {
    try {
      const uri = `${this.storeUrl}${Endpoints.PAYPAL_GET_CLIENT_TOKEN}`;
      const response = await window.fetch(uri);
      if (response.status !== 200) {
        return null;
      }
      const result = (await response.json()) as PayPalAcpGetClientTokenResponse;
      return result.ClientToken || result.clientToken || null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return null;
    }
  };

  public getIsAcpActive = async (): Promise<boolean> => {
    try {
      const uri = `${this.storeUrl}${Endpoints.PAYPAL_PPCP_ACTIVE}`;
      const response = await window.fetch(uri);
      if (response.status === 200) {
        const result = await response.text();
        return result?.toLowerCase() === 'acpeligible';
      }
      return false;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return false;
    }
  };

  public static buildPaymentAddressContainer = (
    address: AddressContract
  ): PayPalPaymentAddressContainer => {
    return {
      shipping_address: {
        recipient_name: `${address.firstName} ${address.lastName}`,
        line1: address.address1,
        line2: address.address2,
        city: address.city,
        country_code: address.country,
        postal_code: address.postalCode,
        state: address.state,
      },
    };
  };

  public static buildPaymentTotalsContainer = (
    totals: CartTotalsContract
  ): PayPalPaymentTotals => {
    const {
      items = 0,
      discounts = 0,
      giftWrap = 0,
      kitItems = 0,
      giftCardAmountUsed = 0,
      giftCards = 0,
      shipping,
      taxTotal,
    } = totals;

    const startingSubTotal = items + discounts + giftWrap + kitItems;
    let calculatedCredit = giftCardAmountUsed + giftCards;

    const calculatedSubtotal = calculateTotalAfterCredit(
      startingSubTotal,
      calculatedCredit
    );
    calculatedCredit = calculateCreditRemaining(
      calculatedCredit,
      startingSubTotal,
      calculatedSubtotal
    );

    const calculatedShipping = calculateTotalAfterCredit(
      shipping,
      calculatedCredit
    );
    calculatedCredit = calculateCreditRemaining(
      calculatedCredit,
      shipping,
      calculatedShipping
    );

    const calculatedTax = calculateTotalAfterCredit(taxTotal, calculatedCredit);
    const calculatedTotal =
      calculatedSubtotal + calculatedShipping + calculatedTax;

    return {
      total: calculatedTotal.toFixed(2),
      subtotal: calculatedSubtotal.toFixed(2),
      shipping: calculatedShipping.toFixed(2),
      tax: calculatedTax.toFixed(2),
    };
  };

  public static buildNewCustomerUpdate = (
    details: PayPalPaymentData
  ): CustomerCreateContract => {
    const { firstName, lastName } = getNamesFromPaymentData(details);
    return {
      firstName,
      lastName,
      email:
        details.payer?.payer_info?.email ?? details.payer?.email_address ?? '',
      password: null,
      anonymous: true,
      emailOptIn: false,
    };
  };

  public static buildAddressUpdate = (
    details: PayPalPaymentData,
    countryName: string | null,
    existingAddress: AddressContract
  ): AddressContract => {
    const paypalCustomer = details.payer?.payer_info;
    const paypalAddress = details.transactions.item_list?.shipping_address;

    const fallbackCountry = paypalAddress?.country_code ?? '';
    const fallbackPhone =
      details.transactions.item_list?.shipping_phone_number ??
      existingAddress.phoneNumber ??
      null;
    const { firstName, lastName } = getNamesFromPaymentData(details);

    return {
      phoneNumber: paypalCustomer?.phone ?? fallbackPhone,
      firstName,
      lastName,
      address1: paypalAddress?.line1 ?? null,
      address2: paypalAddress?.line2 ?? null,
      city: paypalAddress?.city ?? null,
      state: paypalAddress?.state ?? null,
      country: countryName ?? fallbackCountry,
      postalCode: paypalAddress?.postal_code ?? null,
      residential: true,
      id: existingAddress.id,
      companyName: existingAddress.companyName,
      preferred: existingAddress.preferred,
    };
  };

  public static buildPayPalInfoUpdate = (
    details: PayPalPaymentData,
    payerId: string,
    paymentMethodId: number
  ): PayPalInfoContract => {
    const transactionAmount =
      details.transactions.Amount ?? details.transactions.amount;
    return {
      totalAmount:
        parseInt((parseFloat(transactionAmount.total) * 100).toFixed(0), 10) /
        100,
      transactionId: details.id ?? null,
      paymentStatus: details.state ?? null,
      payerId,
      token: details.token ?? null,
      useStoreCredit: false,
      paymentMethodId,
    };
  };

  public static findPayPalPaymentMethod = (
    enabledPaymentMethods: ConfigEnabledPaymentMethodContract[]
  ): ConfigEnabledPaymentMethodContract | undefined => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return enabledPaymentMethods.find((method: any) => {
      return (
        method.methodType && method.methodType.toLowerCase().includes('paypal')
      );
    });
  };

  public static extractCreatePaymentError = (
    container: PayPalRestErrorContainer
  ): GenericStoreError[] => {
    let errors: GenericStoreError[] = [];
    let hasZipError = false;
    if (!container.errors || !container.errors.count) {
      return errors;
    }
    try {
      const paypalError = JSON.parse(
        container.errors.message
      ) as PayPalRestCreatePaymentError;

      let primaryError;
      switch (paypalError.name) {
        case 'VALIDATION_ERROR':
          primaryError = 'PayPal Validation Error';
          break;
        case 'TRANSACTION_REFUSED':
          primaryError = 'PayPal Transaction Error';
          break;
        default:
          primaryError = 'PayPal Error';
          break;
      }

      const hasErrorDetails = paypalError.details && paypalError.details.length;

      const primaryMessage = hasErrorDetails
        ? `${paypalError.message}:`
        : paypalError.message;

      errors.push({ field: primaryError, messages: [primaryMessage] });
      if (hasErrorDetails) {
        // For now, just loop and format all errors. This can be modified to exclude specific errors or
        // translate to more friendly messages
        paypalError.details.forEach(errorDetail => {
          const { field, issue } = errorDetail;

          if (field) {
            errors[0].messages.push(`"${field}": ${issue}`);
          } else {
            errors[0].messages.push(issue);
          }
          // We need to point out that often it is the Zip that is the issue, although
          // PayPal will return invalid city and state as well
          if (field?.toLowerCase() === 'zip') {
            hasZipError = true;
          }
        });
      }
      if (hasZipError) {
        errors[0].messages.push(
          'Please be sure your "Zip" is valid for the city and state entered'
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      errors = [];
      errors.push({
        field: 'PayPal Error',
        messages: ['Unknown PayPal error'],
      });
    }
    return errors;
  };

  public findPayPalPaymentMethod = (
    enabledPaymentMethods: ConfigEnabledPaymentMethodContract[]
  ): ConfigEnabledPaymentMethodContract | undefined => {
    return PayPalService.findPayPalPaymentMethod(enabledPaymentMethods);
  };

  public extractCreatePaymentError = (
    container: PayPalRestErrorContainer
  ): GenericStoreError[] => {
    return PayPalService.extractCreatePaymentError(container);
  };

  public calculate3dsAction = (
    cardResponse: PayPalAcpHostedFieldsSubmitResponse
  ): PayPalAcp3dsAction => {
    const {
      authenticationReason,
      authenticationResult,
      authenticationStatus,
      enrollmentStatus,
      liabilityShift,
      liabilityShifted,
    } = cardResponse;
    const formattedResponse =
      authenticationResult ||
      enrollmentStatus ||
      (liabilityShift && !authenticationReason)
        ? {
            authenticationResult,
            enrollmentStatus,
            liabilityShift,
          }
        : {
            authenticationReason,
            authenticationStatus,
            liabilityShifted,
          };

    return find3dsAction(formattedResponse);
  };

  private sendCreatePayment = async (
    paymentRequest: PayPalPaymentData
  ): Promise<PayPalCreatePaymentResponse> => {
    const createEndpoint = this.useV2Endpoints
      ? Endpoints.PAYPAL_ORDER
      : Endpoints.PAYPAL_CREATE_PAYMENT;
    const response = await window.fetch(`${this.storeUrl}${createEndpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json;charset=UTF-8' },
      body: JSON.stringify(paymentRequest),
    });
    this.cachedDetails = null;
    return response.json();
  };
}

const calculateTotalAfterCredit = (total: number, credit: number): number => {
  return credit > total ? 0 : total - credit;
};
const calculateCreditRemaining = (
  credit: number,
  previousTotal: number,
  newTotal: number
): number => {
  return newTotal > 0 ? 0 : credit - previousTotal;
};

export type GenericStoreError = {
  field: string;
  messages: string[];
};

export type PayPalRestErrorContainer = {
  errors: {
    count: number;
    message: string;
  };
};

export type PayPalRestCreatePaymentError = {
  name: string;
  message: string;
  details: PayPalRestGenericErrorDetails[];
};

export type PayPalRestGenericErrorDetails = {
  field: string;
  issue: string;
};

export const PayPalNotAvailableServiceError = new ServiceError(
  'PayPalService',
  'PayPal is no longer enabled for payment'
);

export const PayPalTransactionNotFoundServiceError = new ServiceError(
  'PayPalService',
  'PayPal transaction not found.'
);

export default PayPalService;
